import * as dat from 'dat.gui';
import GSAP from 'gsap';

export default class EyeRecorder {
	constructor({ aboutSection }) {
		this.about = aboutSection;
		this.active = window.location.hash === '#debug';

		this.state = 'idle';
		this.samples = [];
		this.startTime = 0;
		this.lastX = 0;
		this.lastY = 0;

		this.monitors = {
			state: 'idle',
			time: 0,
			distance: 0,
			x: 0,
			y: 0,
			samples: 0,
		};

		this.onKeyDownBound = this.onKeyDown.bind(this);
		this.onArmingMouseMoveBound = this.onArmingMouseMove.bind(this);

		this.gui = new dat.GUI({ width: 280 });

		if (!this.active) {
			this.gui.hide();
			return;
		}

		this.createGui();
		window.addEventListener('keydown', this.onKeyDownBound);
	}

	createGui() {
		const folder = this.gui.addFolder('Eye Recorder');
		folder.add(this.monitors, 'state').listen();
		folder.add(this.monitors, 'time').listen();
		folder.add(this.monitors, 'distance').listen();
		folder.add(this.monitors, 'x').listen();
		folder.add(this.monitors, 'y').listen();
		folder.add(this.monitors, 'samples').listen();

		const actions = {
			record: () => this.arm(),
			stop: () => this.stop(),
			clear: () => this.clear(),
			copyJson: () => this.copyJson(),
			downloadJson: () => this.downloadJson(),
			play: () => this.play(),
		};
		folder.add(actions, 'record').name('Record (R)');
		folder.add(actions, 'stop').name('Stop (S)');
		folder.add(actions, 'clear').name('Clear');
		folder.add(actions, 'copyJson').name('Copy JSON');
		folder.add(actions, 'downloadJson').name('Download JSON');
		folder.add(actions, 'play').name('Play');
		folder.open();
	}

	onKeyDown(e) {
		const t = e.target;
		if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
		if (e.key === 'r' || e.key === 'R') this.arm();
		else if (e.key === 's' || e.key === 'S') this.stop();
	}

	arm() {
		if (this.state === 'recording' || this.state === 'armed') return;
		this.samples = [];
		this.monitors.distance = 0;
		this.monitors.time = 0;
		this.monitors.samples = 0;
		this.state = 'armed';
		this.monitors.state = 'armed';
		window.addEventListener('mousemove', this.onArmingMouseMoveBound, { once: true });
	}

	onArmingMouseMove() {
		if (this.state !== 'armed') return;
		this.startTime = performance.now();
		this.lastX = this.about?.x ?? 0;
		this.lastY = this.about?.y ?? 0;
		this.state = 'recording';
		this.monitors.state = 'recording';
	}

	stop() {
		window.removeEventListener('mousemove', this.onArmingMouseMoveBound);
		if (this.state !== 'recording' && this.state !== 'armed') return;
		this.state = 'stopped';
		this.monitors.state = 'stopped';
	}

	clear() {
		this.samples = [];
		this.monitors.distance = 0;
		this.monitors.time = 0;
		this.monitors.samples = 0;
		this.monitors.x = 0;
		this.monitors.y = 0;
		this.state = 'idle';
		this.monitors.state = 'idle';
	}

	update() {
		if (this.state !== 'recording' || !this.about) return;
		const x = this.about.x;
		const y = this.about.y;
		const t = performance.now() - this.startTime;
		const dx = x - this.lastX;
		const dy = y - this.lastY;
		this.monitors.distance += Math.hypot(dx, dy);
		this.monitors.time = t;
		this.monitors.x = x;
		this.monitors.y = y;
		this.samples.push({ t, x, y });
		this.monitors.samples = this.samples.length;
		this.lastX = x;
		this.lastY = y;
	}

	serialize() {
		return JSON.stringify(
			{
				duration: this.monitors.time,
				distance: this.monitors.distance,
				samples: this.samples,
			},
			null,
			2
		);
	}

	async copyJson() {
		if (!this.samples.length) return console.warn('[EyeRecorder] No samples to copy');
		try {
			await navigator.clipboard.writeText(this.serialize());
			console.log(`[EyeRecorder] Copied ${this.samples.length} samples to clipboard`);
		} catch (err) {
			console.error('[EyeRecorder] Clipboard failed:', err);
		}
	}

	downloadJson() {
		if (!this.samples.length) return console.warn('[EyeRecorder] No samples to download');
		const blob = new Blob([this.serialize()], { type: 'application/json' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `eye-recording-${Date.now()}.json`;
		a.click();
		URL.revokeObjectURL(url);
	}

	play() {
		if (!this.samples.length || !this.about) return;
		const eyes = [this.about.elements?.eyeLeft, this.about.elements?.eyeRight].filter(Boolean);
		if (!eyes.length) return;

		this.about.playback = true;
		this.state = 'playing';
		this.monitors.state = 'playing';

		const tl = GSAP.timeline({
			onComplete: () => {
				this.about.playback = false;
				this.state = 'stopped';
				this.monitors.state = 'stopped';
			},
		});
		const first = this.samples[0];
		tl.set(eyes, { x: first.x, y: first.y });
		for (let i = 1; i < this.samples.length; i++) {
			const s = this.samples[i];
			const prev = this.samples[i - 1];
			const dt = Math.max(0, (s.t - prev.t) / 1000);
			tl.to(eyes, { x: s.x, y: s.y, duration: dt, ease: 'none' });
		}
	}

	destroy() {
		window.removeEventListener('keydown', this.onKeyDownBound);
		window.removeEventListener('mousemove', this.onArmingMouseMoveBound);
		this.gui?.destroy();
	}
}
